import PropTypes from 'prop-types';
import React, {createContext, useContext, useEffect, useState} from 'react';
import type {ThemeMode} from 'Theme/buildAntdTheme';

/**
 * App-wide light/dark mode toggle. Persisted to localStorage so it survives
 * reloads; falls back to the OS/browser color-scheme preference on first
 * visit, and to 'light' if neither is available (private browsing, old
 * browsers, or — in tests — a JSDOM environment without matchMedia).
 */

const STORAGE_KEY = 'ast-theme-mode';

interface ThemeModeContextValue {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'light',
  toggleMode: () => {},
  setMode: () => {},
});

const getInitialMode = (): ThemeMode => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch (error) {
    // localStorage unavailable (private browsing, disabled storage, etc.) — fall through.
  }
  try {
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  } catch (error) {
    // matchMedia unavailable — default to light below.
  }
  return 'light';
};

const ThemeModeProvider = ({children}) => {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch (error) {
      // ignore persistence failures — mode still works for the current session
    }
  }, [mode]);

  const toggleMode = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));

  return (
    <ThemeModeContext.Provider value={{mode, toggleMode, setMode}}>
      {children}
    </ThemeModeContext.Provider>
  );
};

const useThemeMode = (): ThemeModeContextValue => useContext(ThemeModeContext);

ThemeModeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export {ThemeModeProvider, ThemeModeContext, useThemeMode};
